import type { OrgProjectOption, ProjectScope } from "@/stores/organizations/repository";

/**
 * A picker row. `unavailable` marks a project fetchOrgProjects deliberately
 * leaves out (archived/hidden project, archived building) that the member is
 * nonetheless assigned to: it still gets a checkbox, so the access stays
 * revocable, but is labelled so nobody reads it as an offerable project.
 *
 * The flag comes from the resolved row's own columns, never from "the offer
 * list didn't have it" — see ResolvedProjectOption in repository.ts.
 */
export type PickerProject = OrgProjectOption & { unavailable?: boolean };

/**
 * Split a member's stored project assignments by whether a list of project ids
 * covers them.
 */
export function splitAssignments(
  offeredProjectIds: number[],
  assignedIds: number[]
): { visible: number[]; hidden: number[] } {
  const offered = new Set(offeredProjectIds);
  return {
    visible: assignedIds.filter((id) => offered.has(id)),
    hidden: assignedIds.filter((id) => !offered.has(id)),
  };
}

/** A picker's tick state: the projects ticked, and the folders unticked. */
export interface PickerSelection {
  selectedIds: number[];
  excludedFolderIds: number[];
}

/** A save: the scope to assert, plus the ticks this sitting added. */
export interface ProjectScopeSave {
  scope: ProjectScope;
  /**
   * Projects newly ticked. Only these need re-checking against live rows before
   * the write — the rest of the scope either was already stored or narrows it.
   */
  granted: number[];
}

/**
 * Build the end state a picker session asserts, and name the grants inside it.
 *
 * The RPC reconciles to exactly what it is sent (see
 * repository.ts#setSubcontractorProjects), so the scope has to be the WHOLE
 * picture, not the part the admin moved. Two things therefore ride along that no
 * checkbox represents:
 *
 *   - `keptOutOfPickerIds` — assignments neither project query resolved
 *     (PostgREST's row cap, a narrower RLS policy). They get no row and no tick,
 *     and re-sending them verbatim is what leaves them alone; omitting them
 *     would revoke access on the strength of a query coming back short.
 *   - Folder denials on projects the picker did not render, which arrive in the
 *     seeded exclusion set and stay in it untouched, for the same reason.
 *
 * Denials on a project this save leaves unticked do NOT ride along. The RPC
 * resolves each folder's project itself and drops anything outside the
 * assignments it just wrote, so such an id is never written — but it is not
 * merely inert on the way there: the reduction below covers a denial with any
 * denied ancestor, and parent_id crosses projects, so a doomed id left in can
 * suppress a denial in a project the save KEEPS and re-grant that folder. The
 * caller prunes to the carried projects before reducing (see handleSave in
 * ManageSubcontractorProjectsDialog).
 *
 * The deny-list arrives here reduced to the branches an admin actually unticked
 * (`maximalExclusions`), not as the closed set the picker draws from. The two
 * mean the same thing to the server, which expands every id it is given over its
 * own subtree; the reduction is what keeps the payload the size of the ticks
 * rather than the size of the tree. The picker still holds the set closed from
 * both ends — `subtreeClosedExclusions` on mount, `toggleFolderExclusion` on
 * every edit — because that is what the CHECKBOXES are drawn from.
 */
export function buildProjectScopeSave(
  current: PickerSelection,
  seededProjectIds: number[],
  keptOutOfPickerIds: number[]
): ProjectScopeSave {
  const seeded = new Set(seededProjectIds);
  return {
    scope: {
      projectIds: [...new Set([...current.selectedIds, ...keptOutOfPickerIds])],
      excludedFolderIds: current.excludedFolderIds,
    },
    granted: current.selectedIds.filter((id) => !seeded.has(id)),
  };
}

export interface PickerAssignments {
  /** Every row to render, offerable ones first. */
  projects: PickerProject[];
  /** Ids to tick — every assignment that got a row. */
  selectedIds: number[];
  /** Assignments with no row; Save re-sends these so the RPC leaves them alone. */
  keptOutOfPickerIds: number[];
}

/**
 * Build the picker's rows from the org's offerable projects and the member's
 * stored assignments.
 *
 * An assignment the offer list leaves out is one of two very different things,
 * and conflating them is an access-control bug either way:
 *
 *   deliberately excluded — an archived/hidden project, or one in an archived
 *     building. Archiving does not revoke access, so an owner/admin must still
 *     be able to take it away. `resolveExcluded` looks those ids up directly
 *     (fetchProjectsByIds ignores the offerable filters), and they render as
 *     ticked, labelled rows that can be unticked like any other.
 *   genuinely absent — neither query returned it (PostgREST's row cap, a
 *     narrower RLS policy). Absence is evidence of nothing there, so the id gets
 *     no tick and no row — and, because Save asserts an end state, it is handed
 *     back as `keptOutOfPickerIds` and re-sent verbatim (buildProjectScopeSave).
 *     Dropping it from the payload would revoke it.
 *
 * A third case sits between them: the id resolves, but the row is live — the
 * offer list was short of it for the same row-cap/RLS reasons, not because
 * anything was archived. Such a row is restored to the picker ticked and
 * *unlabelled*, so it never reads as "archived or hidden" to an admin deciding
 * whether to untick it. Which of the two it is comes from the resolved row's
 * own `unavailable` flag; this function no longer assumes.
 */
export async function buildPickerAssignments(
  offeredProjects: OrgProjectOption[],
  assignedIds: number[],
  resolveExcluded: (ids: number[]) => Promise<PickerProject[]>
): Promise<PickerAssignments> {
  const { visible, hidden } = splitAssignments(
    offeredProjects.map((project) => project.id),
    assignedIds
  );

  const excluded = await resolveExcluded(hidden);
  const { visible: revocable, hidden: unresolved } = splitAssignments(
    excluded.map((project) => project.id),
    hidden
  );

  return {
    projects: [...offeredProjects, ...excluded],
    selectedIds: [...visible, ...revocable],
    keptOutOfPickerIds: unresolved,
  };
}
